import {
  isLoaded,
  isReserved,
  PolicyNumber,
  PolicyNumberError,
  PolicyNumberValidating,
  Reserved,
  UserAssignedPolicyNumber,
} from 'ApplicationEntry/models/PolicyNumber';
import {
  EmptyPolicyTypes,
  isLoadedPolicyTypes,
  LoadedPolicyTypes,
  PolicyTypes,
} from 'ApplicationEntry/models/PolicyTypes';
import {Async, Loaded} from 'Core/models/Async';
import {ToPartialMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';
import {policyNumberDetail} from './GroupCaseDetails';
import { EmptyString, StringUnion } from 'Core/models/EmptyString';
import { policyStatus } from 'ApplicationEntry/api/awdApi';

export class InvalidOrdinaryCaseDetails {
  public readonly key = 'InvalidOrdinaryCaseDetails';

  constructor(
    public readonly policyNumber: PolicyNumber,
    public readonly productType: PolicyTypes,
    public readonly disableNexttoShowOtherSection: boolean,
    public readonly disableNexttoShowOtherSectionArr: policyNumberDetail[],
    public readonly achIndicator: boolean,
    public readonly policyStatus: StringUnion,
    public readonly managedStatus: StringUnion,
    public readonly managedStatusFlag: boolean,
  ) {}
}

export class ValidOrdinaryCaseDetails {
  public static create(
    policyNumber: PolicyNumber,
    productType: PolicyTypes,
    disableNexttoShowOtherSection: boolean,
    disableNexttoShowOtherSectionArr: policyNumberDetail[],
    achIndicator: boolean,
    policyStatus: StringUnion,
    managedStatus: StringUnion,
    managedStatusFlag: boolean,
  ) {
    return isLoadedPolicyTypes(productType) &&
      productType.selectedPolicyTypes.length > 0 &&
      productType.selectedPolicyTypes.length <= 3 &&
      (isLoaded(policyNumber) || isReserved(policyNumber))
      ? new ValidOrdinaryCaseDetails(
          policyNumber,
          productType,
          disableNexttoShowOtherSection,
          disableNexttoShowOtherSectionArr,
          achIndicator,
          policyStatus,
          managedStatus,
          managedStatusFlag,
        )
      : new InvalidOrdinaryCaseDetails(
          policyNumber,
          productType,
          disableNexttoShowOtherSection,
          disableNexttoShowOtherSectionArr,
          achIndicator,
          policyStatus,
          managedStatus,
          managedStatusFlag
        );
  }

  public readonly key = 'ValidOrdinaryCaseDetails';
  private constructor(
    public readonly policyNumber: Loaded<string> | Reserved,
    public readonly productType: LoadedPolicyTypes,
    public readonly disableNexttoShowOtherSection: boolean,
    public readonly disableNexttoShowOtherSectionArr: policyNumberDetail[],
    public readonly achIndicator: boolean,
    public readonly policyStatus: StringUnion,
    public readonly managedStatus: StringUnion,
    public readonly managedStatusFlag: boolean,
  ) {}
}

export type OrdinaryCaseDetails = ValidOrdinaryCaseDetails | InvalidOrdinaryCaseDetails;

export type OrdinaryCaseDetailsOptions<TReturn> = ToPartialMatchOptions<OrdinaryCaseDetails, 'key', TReturn>;

const generatedisableNexttoShowOtherSectionArr = (
  disableNexttoShowOtherSection: boolean,
  enteredPolicyNumber: string,
  disableNexttoShowOtherSectionArr: policyNumberDetail[]
) => {
  if (disableNexttoShowOtherSectionArr?.length === 0) {
    return [
      ...disableNexttoShowOtherSectionArr,
      {policyNumberExists: disableNexttoShowOtherSection, policyNo: enteredPolicyNumber},
    ];
  } else if (
    disableNexttoShowOtherSectionArr?.length > 0 &&
    disableNexttoShowOtherSectionArr.some(policyNumber => policyNumber.policyNo === enteredPolicyNumber)
  ) {
    return disableNexttoShowOtherSectionArr.filter(policyNumber => policyNumber.policyNo === enteredPolicyNumber);
  } else if (
    disableNexttoShowOtherSectionArr?.length > 0 &&
    disableNexttoShowOtherSectionArr?.some(
      policyNumber =>
        policyNumber.policyNo.substring(0, 2) === enteredPolicyNumber.substring(0, 2) &&
        policyNumber.policyNo.charAt(policyNumber.policyNo.length - 1) ===
          enteredPolicyNumber.charAt(enteredPolicyNumber.length - 1)
    )
  ) {
    let matchedPolciyArr = disableNexttoShowOtherSectionArr.filter(
      policyNumber =>
        policyNumber.policyNo.substring(0, 2) === enteredPolicyNumber.substring(0, 2) &&
        policyNumber.policyNo.charAt(policyNumber.policyNo.length - 1) ===
          enteredPolicyNumber.charAt(enteredPolicyNumber.length - 1)
    );
    matchedPolciyArr?.map((item, idx) => {
      item.policyNo = enteredPolicyNumber;
      item.policyNumberExists = disableNexttoShowOtherSection;
    });

    return matchedPolciyArr;
  } else {
    return [
      ...disableNexttoShowOtherSectionArr,
      {policyNumberExists: disableNexttoShowOtherSection, policyNo: enteredPolicyNumber},
    ];
  }
};

export const matchOrdinaryCaseDetails = <TReturn>(
  caseDetails: OrdinaryCaseDetails,
  options: OrdinaryCaseDetailsOptions<TReturn>
): TReturn => {
  switch (caseDetails.key) {
    case 'InvalidOrdinaryCaseDetails':
      return options.InvalidOrdinaryCaseDetails
        ? options.InvalidOrdinaryCaseDetails(caseDetails)
        : options.default(caseDetails);
    case 'ValidOrdinaryCaseDetails':
      return options.ValidOrdinaryCaseDetails
        ? options.ValidOrdinaryCaseDetails(caseDetails)
        : options.default(caseDetails);
  }
};

export const changePolicyNumber = (policyNumber: Async<string>) => ({
  productType,
  disableNexttoShowOtherSection,
  disableNexttoShowOtherSectionArr,
  achIndicator,
  policyStatus,
  managedStatus,
  managedStatusFlag,
}: OrdinaryCaseDetails) =>
  ValidOrdinaryCaseDetails.create(
    policyNumber,
    productType,
    disableNexttoShowOtherSection,
    disableNexttoShowOtherSectionArr?.length > 0 &&
      disableNexttoShowOtherSectionArr.some(policyNumber => policyNumber.policyNumberExists === true)
      ? (disableNexttoShowOtherSectionArr = [])
      : [],
    achIndicator,
    policyStatus,
    managedStatus,
    managedStatusFlag
  );
export const changeUserPolicyNumber = (policyNumber: string) => ({
  productType,
  disableNexttoShowOtherSection,
  disableNexttoShowOtherSectionArr,
  achIndicator,
  policyStatus,
  managedStatus,
  managedStatusFlag,
}: OrdinaryCaseDetails) =>
  ValidOrdinaryCaseDetails.create(
    UserAssignedPolicyNumber.create(policyNumber),
    productType,
    disableNexttoShowOtherSection,
    disableNexttoShowOtherSectionArr,
    achIndicator,
    policyStatus,
    managedStatus,
    managedStatusFlag
  );

export const changeReservedPolicyNumber = (policyNumber: string) => ({
  productType,
  disableNexttoShowOtherSection,
  disableNexttoShowOtherSectionArr,
  achIndicator,
  policyStatus,
  managedStatus,
  managedStatusFlag,
}: OrdinaryCaseDetails) =>
  ValidOrdinaryCaseDetails.create(
    Reserved.create(policyNumber),
    productType,
    disableNexttoShowOtherSection,
    disableNexttoShowOtherSectionArr,
    achIndicator,
    policyStatus,
    managedStatus,
    managedStatusFlag
  );

export const changeProductType = (productType: PolicyTypes) => ({
  policyNumber,
  disableNexttoShowOtherSection,
  disableNexttoShowOtherSectionArr,
  achIndicator,
  policyStatus,
  managedStatus,
  managedStatusFlag,
}: OrdinaryCaseDetails) =>
  ValidOrdinaryCaseDetails.create(
    policyNumber,
    productType,
    disableNexttoShowOtherSection,
    disableNexttoShowOtherSectionArr,
    achIndicator,
    policyStatus,
    managedStatus,
    managedStatusFlag
  );
export const changePolicyNumberError = (policyNumber: string) => ({
  productType,
  disableNexttoShowOtherSection,
  disableNexttoShowOtherSectionArr,
  achIndicator,
  policyStatus,
  managedStatus,
  managedStatusFlag,
}: OrdinaryCaseDetails) =>
  ValidOrdinaryCaseDetails.create(
    PolicyNumberError.create(policyNumber),
    productType,
    disableNexttoShowOtherSection,
    disableNexttoShowOtherSectionArr,
    achIndicator,
    policyStatus,
    managedStatus,
    managedStatusFlag
  );
export const changePolicyNumberValidation = (policyNumber: string) => ({
  productType,
  disableNexttoShowOtherSection,
  disableNexttoShowOtherSectionArr,
  achIndicator,
  policyStatus,
  managedStatus,
  managedStatusFlag,
}: OrdinaryCaseDetails) =>
  ValidOrdinaryCaseDetails.create(
    PolicyNumberValidating.create(policyNumber),
    productType,
    disableNexttoShowOtherSection,
    disableNexttoShowOtherSectionArr,
    achIndicator,
    policyStatus,
    managedStatus,
    managedStatusFlag
  );

export const getPolicyNumberExists = (disableNexttoShowOtherSection: boolean, enteredPolicyNumber: string) => ({
  policyNumber,
  productType,
  disableNexttoShowOtherSectionArr,
  achIndicator,
  policyStatus,
  managedStatus,
  managedStatusFlag,
}: OrdinaryCaseDetails) =>
  ValidOrdinaryCaseDetails.create(
    policyNumber,
    productType,
    disableNexttoShowOtherSection,
    generatedisableNexttoShowOtherSectionArr(
      disableNexttoShowOtherSection,
      enteredPolicyNumber,
      disableNexttoShowOtherSectionArr,
    ),
    achIndicator,
    policyStatus,
    managedStatus,
    managedStatusFlag
  );

export const EmptyOrdinaryCaseDetails = new InvalidOrdinaryCaseDetails(
  new UserAssignedPolicyNumber(''),
  EmptyPolicyTypes,
  false,
  [],
  false,
  new EmptyString(),
  new EmptyString(),
  false
);
